import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React, { useRef, useState } from 'react';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { ModalFooter } from 'src/modules/shared/components/molecules/modals/modal-footer';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  type CatalogProductBulkImportBodyParams,
  type CatalogProductBulkImportResponse,
  CustomFieldEntityType,
} from 'src/api/generated';
import { useProductImport } from 'src/modules/shared/hooks/api/catalog/products.mutation';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { useAlert } from 'src/contexts/alert-context';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiDownloadSimple, PiUploadSimple } from 'react-icons/pi';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { useCustomFieldDefinitions } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { useProductImport as useProductImportLogic } from 'src/modules/catalog/hooks/product-import';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { PiQuestion } from 'react-icons/pi';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import Tag from 'src/modules/shared/components/atoms/tag';

interface ProductImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const exampleCsv = `sourceId;name;description;price;currency;sourceUrl;customFields.string_list_categoria
"ml-12345";"Departamento Las Condes";"Departamento 2D/2B con estacionamiento";4500;UF;https://ejemplo.cl/departamento;residencial`;

type ProductField =
  | 'id'
  | 'sourceId'
  | 'name'
  | 'description'
  | 'price'
  | 'currency'
  | 'sourceUrl';

const productFieldLabels: Record<ProductField, string> = {
  id: 'ID (opcional)',
  sourceId: 'ID de origen (opcional)',
  name: 'Nombre',
  description: 'Descripción',
  price: 'Precio',
  currency: 'Moneda',
  sourceUrl: 'URL de origen',
};

export const ProductImportModal: React.FC<ProductImportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { showAlert } = useAlert();
  const [result, setResult] = useState<CatalogProductBulkImportResponse | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { definitions: customFieldDefinitions, loading: loadingCustomFields } =
    useCustomFieldDefinitions({
      entityType: CustomFieldEntityType.PRODUCT,
      pageSize: 200,
    });

  const [
    {
      fileName,
      headers,
      rows,
      previewRows,
      fieldMapping,
      customFieldMapping,
      parseError,
    },
    {
      handleFileChange: parseFileChange,
      reset,
      buildItems,
      validateRowLimit,
      setFieldMapping,
      setCustomFieldMapping,
      setParseError,
    },
  ] = useProductImportLogic(customFieldDefinitions);

  const { importProducts, loading, error } = useProductImport({
    onSuccess: () => {
      // TODO: check when implementing i18n
      showAlert('Productos importados correctamente', 'success');
      handleClose();
    },
    onError: (importError) => {
      const message = extractApiErrorMessage(importError);
      showAlert(message, 'error');
    },
  });

  const downloadTemplate = () => {
    const blob = new Blob([exampleCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'productos-example.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setResult(null);
    try {
      await parseFileChange(event);
    } catch (err) {
      const message =
        // TODO: check when implementing i18n
        err instanceof Error ? err.message : 'No se pudo leer el archivo.';
      setParseError(message);
    }
  };

  const handleImport = async () => {
    if (rows.length === 0) {
      // TODO: check when implementing i18n
      setParseError('Primero selecciona un archivo con datos.');
      return;
    }

    const rowLimitError = validateRowLimit();
    if (rowLimitError) {
      setParseError(rowLimitError);
      return;
    }

    const body: CatalogProductBulkImportBodyParams = {
      items: buildItems(),
    };

    await importProducts(body);
  };

  const handleClose = () => {
    setResult(null);
    setParseError(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      dialogClassName="sm:max-w-5xl"
      header={
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Text
              size="h4"
              weight="semibold"
              className="flex items-center gap-2"
            >
              {/* TODO: check when implementing i18n */}
              Importar productos
            </Text>

            <Tag
              content="CSV / Excel"
              size="sm"
              variant="info"
              appearance="filled"
            />
          </div>

          <Text size="small" color="secondary">
            {/* TODO: check when implementing i18n */}
            Sube un archivo con encabezado. Máximo 1000 productos por
            importación.
          </Text>
        </div>
      }
      body={
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-3 shadow-sm">
            <Button
              appearance="filled"
              variant="primary"
              size="md"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2"
            >
              <PiUploadSimple />
              {/* TODO: check when implementing i18n */}
              Seleccionar CSV / Excel
            </Button>
            {fileName && (
              <Tag
                content={`${fileName}`}
                size="md"
                variant="primary"
                appearance="filled"
                className="mr-4"
              />
            )}

            <Button
              appearance="soft"
              variant="primary"
              size="md"
              onClick={downloadTemplate}
              className="flex items-center gap-2"
            >
              <PiDownloadSimple />
              {/* TODO: check when implementing i18n */}
              Descargar ejemplo
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleFileChange}
          />

          {headers.length > 0 && (
            <Card appearance="outline" variant="secondary">
              <CardHeader className="flex items-center justify-between">
                <Text size="small" weight="medium">
                  {/* TODO: check when implementing i18n */}
                  Vista previa (primeros {previewRows.length} registros)
                </Text>
                <Text size="tiny" weight="regular" color="secondary">
                  {/* TODO: check when implementing i18n */}
                  Columnas detectadas: {headers.length}
                </Text>
              </CardHeader>
              <CardBody className="p-0">
                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white">
                      <tr>
                        {headers.map((header) => (
                          <th
                            key={header}
                            className="text-left font-medium text-gray-700 px-3 py-2 border-b border-gray-100"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, rowIndex) => (
                        <tr
                          key={`row-${rowIndex}`}
                          className="bg-white even:bg-gray-50"
                        >
                          {headers.map((header) => (
                            <td
                              key={header}
                              className="px-3 py-2 text-gray-800 border-b border-gray-100"
                            >
                              {row[header] ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}

          {headers.length > 0 && (
            <Card appearance="outline" variant="secondary">
              <CardHeader className="flex items-center justify-between">
                <Text size="small" weight="medium">
                  {/* TODO: check when implementing i18n */}
                  Mapea los campos de tu archivo a los campos del producto
                </Text>
              </CardHeader>
              <CardBody className="grid gap-3 sm:grid-cols-2">
                {(Object.keys(productFieldLabels) as ProductField[]).map(
                  (field) => (
                    <div
                      key={field}
                      className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50/50 p-3"
                    >
                      <div className="flex items-center gap-2">
                        <Text size="small" weight="medium">
                          {productFieldLabels[field]}
                          {field === 'name' && (
                            <span className="text-danger-500"> *</span>
                          )}
                        </Text>
                        {field === 'id' && (
                          <Tooltip
                            // TODO: check when implementing i18n
                            content="Si mapeas el ID, se actualizarán los productos existentes con ese ID. Si no lo mapeas, se crearán o actualizarán por nombre (o por sourceId si está configurado)."
                            placement="top"
                            size="sm"
                          >
                            <PiQuestion />
                          </Tooltip>
                        )}
                        {field === 'sourceId' && (
                          <Tooltip
                            // TODO: check when implementing i18n
                            content="Usa un identificador estable de tu sistema externo (por ejemplo, el ID de MercadoLibre). Si el sourceId ya existe, el producto se actualiza; si es nuevo, se crea."
                            placement="top"
                            size="sm"
                          >
                            <PiQuestion />
                          </Tooltip>
                        )}
                      </div>
                      <Select
                        value={fieldMapping[field] ?? ''}
                        onChange={(event) =>
                          setFieldMapping((prev) => ({
                            ...prev,
                            [field]: event.target.value || undefined,
                          }))
                        }
                        options={[
                          { value: '', content: 'No mapear' },
                          ...headers.map((header) => ({
                            value: header,
                            content: header,
                          })),
                        ]}
                        size="md"
                        containerClassName="sm:max-w-sm"
                      />
                    </div>
                  )
                )}

                {loadingCustomFields ? (
                  <Text size="small" color="secondary">
                    Cargando campos personalizados...
                  </Text>
                ) : customFieldDefinitions.length === 0 ? null : (
                  customFieldDefinitions.map((definition) => (
                    <div
                      key={definition.id}
                      className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50/50 p-3"
                    >
                      <Text size="small" weight="medium">
                        {nonEmptyString(definition.fieldLabel) ??
                          definition.fieldKey}
                      </Text>
                      <Select
                        value={customFieldMapping[definition.id] ?? ''}
                        onChange={(event) =>
                          setCustomFieldMapping((prev) => ({
                            ...prev,
                            [definition.id]: event.target.value || undefined,
                          }))
                        }
                        options={[
                          { value: '', content: 'No mapear' },
                          ...headers.map((header) => ({
                            value: header,
                            content: header,
                          })),
                        ]}
                        size="md"
                        containerClassName="sm:max-w-sm"
                      />
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          )}

          {parseError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
              {parseError}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
              {extractApiErrorMessage(error)}
            </div>
          )}
          {result && (
            <Card appearance="soft" variant="secondary">
              <CardBody className="p-0 flex flex-col gap-2">
                <Text size="small" weight="medium">
                  {/* TODO: check when implementing i18n */}
                  Resumen
                </Text>
                <Text size="small" color="secondary">
                  {/* TODO: check when implementing i18n */}
                  <strong>Creado:</strong> {result.created} /{' '}
                  <strong>Actualizado:</strong> {result.updated}{' '}
                  {result.failed ? (
                    <>
                      / <strong>Fallidos:</strong> {result.failed}
                    </>
                  ) : null}
                </Text>
                {result.errors && (
                  <div className="mt-2 space-y-1">
                    {result.errors.map((item) => (
                      <Text key={item.index} size="small" color="danger">
                        #{item.index + 1}: {item.message}
                      </Text>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      }
      footer={
        <ModalFooter
          onCancel={handleClose}
          onConfirm={handleImport}
          confirmText="Importar"
          cancelText="Cancelar"
          loading={loading}
        />
      }
    />
  );
};
